/**
 * BackfillProgressView — Story SCH-3.2.
 *
 * Renders inside BackfillPreviewModal once the modal switches to "progress"
 * mode. Polls /sync-progress every 3s; shows progress bar, counters, status
 * pill, recent-failures accordion, and the role-gated Continue / Resume CTAs.
 *
 * Role gate: Continue and Resume buttons are enabled for admins + superadmins
 * (manage_settings / system.override — the Settings → Integrations permission
 * set; see useCanRunBackfill()). Users without admin access see disabled
 * buttons with an explanatory tooltip — they retain read-only progress
 * visibility.
 */

import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { formatDistanceToNow, parseISO } from 'date-fns';
import {
  AlertTriangle,
  CheckCircle2,
  Loader2,
  PauseCircle,
  PlayCircle,
  XCircle,
} from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { supabase } from '@/lib/supabaseClient';
import {
  useAbortBackfill,
  useCanRunBackfill,
  useContinueBackfill,
  useSyncProgress,
} from '@/hooks/integrations/useBackfillProgress';
import { useToast } from '@/components/ui/use-toast';
import type {
  SyncProgressFailure,
  SyncProgressResponse,
  SyncProgressStatus,
} from '@/types/integrations/backfillProgress';

interface BackfillProgressViewProps {
  syncRunId: string;
  onClose: () => void;
  /** SCH-3.5: when true, hide all action CTAs (Continue / Resume / Done).
   * Used by "View last run" entry point on already-finished runs. */
  readOnly?: boolean;
}

const ADMIN_REQUIRED_TOOLTIP =
  "Administrator access is required to release the next batch.";

export function BackfillProgressView({
  syncRunId,
  onClose,
  readOnly = false,
}: BackfillProgressViewProps) {
  const { data: progressResult, isLoading } = useSyncProgress(syncRunId);
  const { canRun, loading: roleLoading } = useCanRunBackfill();
  const continueMutation = useContinueBackfill();
  // SCH-3.5: which counter card is drilled-down (null = none expanded).
  const [drillDown, setDrillDown] = useState<DrillDownCategory | null>(null);
  // SCH-3.5: race-guard state. Stays true after a Continue click until the
  // poll cycle confirms the orchestrator has moved on. Closes the window
  // where `continueMutation.isPending` is already false but progress.status
  // hasn't yet transitioned away from awaiting_continue.
  const [pendingContinueClick, setPendingContinueClick] = useState(false);
  // SCH-3.9: per-click "upgrade to auto-continue" toggle next to the
  // Continue button. When checked + Continue clicked, the backend flips
  // the run into auto-continue mode for the remainder. Default off.
  const [upgradeToAutoContinue, setUpgradeToAutoContinue] = useState(false);
  // SCH-4.4: inline two-step abort confirmation state + mutation.
  // (Originally an AlertDialog; replaced with inline UI because Radix
  // dialogs nested inside other Radix dialogs trap focus and become
  // unreachable — the abort confirm UI darkened the backdrop but the
  // confirm controls weren't clickable.)
  const [showAbortConfirm, setShowAbortConfirm] = useState(false);
  const abortMutation = useAbortBackfill();
  const { toast } = useToast();

  // Self-tick to keep relative-time labels ("started X ago", "failed Y ago")
  // fresh independent of the poll cadence. React Query uses structural
  // sharing, so when /sync-progress returns equivalent data it skips a
  // re-render — and once a run finishes, polling stops entirely. Without
  // this tick the timer freezes at its last value. 10s is well under the
  // 30s threshold where date-fns flips "less than a minute" → "1 minute".
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((n) => n + 1), 10_000);
    return () => clearInterval(id);
  }, []);

  // Derive progress safely for the race-guard useEffect below. progressResult
  // may be undefined until the first poll lands; optional chaining keeps
  // currentStatus undefined in that case, which is harmless for the effect
  // (it guards on `pendingContinueClick` first and on specific status values
  // second — undefined matches neither, so it no-ops).
  const progress = progressResult?.data;
  const currentStatus = progress?.status;

  // Reset the race-guard flag when the orchestrator has clearly moved on:
  // either the next batch is processing, the run terminated, or we landed
  // back at awaiting_continue (i.e., the next batch's projects all finished
  // and now we're ready for the user to click again).
  //
  // MUST be called unconditionally before any early returns to satisfy the
  // Rules of Hooks — previously sat below the three early returns and
  // caused a "Rendered more hooks than during the previous render" crash
  // when isLoading flipped from true to false.
  useEffect(() => {
    if (!pendingContinueClick) return;
    if (
      currentStatus === 'processing_batch' ||
      currentStatus === 'fetching_catalog' ||
      currentStatus === 'completed' ||
      currentStatus === 'paused' ||
      currentStatus === 'failed'
    ) {
      setPendingContinueClick(false);
    }
    // Note: NOT awaiting_continue — if we just clicked Continue, the
    // status might still read awaiting_continue for one poll cycle before
    // the orchestrator picks it up. Keep the guard until status visibly
    // changes, then re-enable when we see awaiting_continue again next time.
  }, [currentStatus, pendingContinueClick]);

  if (isLoading && !progressResult) {
    return (
      <div className="flex flex-col items-center gap-3 py-12">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
        <p className="text-sm text-muted-foreground">Fetching backfill status…</p>
      </div>
    );
  }

  if (progressResult?.error) {
    return (
      <div className="flex flex-col items-center gap-3 py-12 text-center">
        <XCircle className="h-8 w-8 text-destructive" />
        <p className="text-sm font-medium">{progressResult.error.message}</p>
        <Button variant="outline" size="sm" onClick={onClose}>
          Close
        </Button>
      </div>
    );
  }

  if (!progress) {
    return (
      <div className="py-12 text-center text-sm text-muted-foreground">
        No backfill data.
      </div>
    );
  }

  const handleContinue = () => {
    if (continueMutation.isPending || pendingContinueClick) return;
    setPendingContinueClick(true);
    continueMutation.mutate({
      syncRunId,
      autoContinue: upgradeToAutoContinue,
    });
  };

  // Resume a run that was started in auto-continue mode but stalled at a batch
  // boundary (the background task died — Fly idle-stop / restart / crash).
  // Re-arms auto-continue so the chain picks back up from the persisted cursor
  // without the admin having to babysit Continue clicks again.
  const handleResumeAutoContinue = () => {
    if (continueMutation.isPending || pendingContinueClick) return;
    setPendingContinueClick(true);
    continueMutation.mutate({ syncRunId, autoContinue: true });
  };

  // SCH-4.4: abort the run. The user has already confirmed via the inline
  // two-step UI; this fires the POST + collapses the confirm UI. The
  // orchestrator picks up the marker between iterations (~30-60s) and
  // pauses the run cleanly.
  const handleAbort = () => {
    if (abortMutation.isPending) return;
    abortMutation.mutate(syncRunId, {
      onSuccess: (result) => {
        setShowAbortConfirm(false);
        if (result.error) {
          toast({
            title: 'Abort failed',
            description: result.error.message,
            variant: 'destructive',
          });
          return;
        }
        toast({
          title: 'Abort requested',
          description:
            'The current batch will finish, then the run will pause. You can resume from the paused state if needed.',
        });
      },
      onError: (err: unknown) => {
        setShowAbortConfirm(false);
        toast({
          title: 'Abort failed',
          description: err instanceof Error ? err.message : 'Unknown error',
          variant: 'destructive',
        });
      },
    });
  };

  // SCH-4.4: show the Abort button when the run is in-flight (any status
  // that's not completed/paused/aborted) AND admin can run AND we're not
  // in read-only mode.
  const showAbortButton =
    !readOnly &&
    canRun &&
    !roleLoading &&
    progress.status !== 'completed' &&
    progress.status !== 'paused';

  const continueDisabled =
    !canRun ||
    roleLoading ||
    continueMutation.isPending ||
    pendingContinueClick ||
    progress.status === 'processing_batch';

  return (
    <div className="flex flex-col gap-5">
      {/* Status pill + heading */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <_StatusPill status={progress.status} />
          {progress.fallbackOwnerUsedCount > 0 && (
            <Badge variant="outline" className="border-yellow-300 text-yellow-700">
              {progress.fallbackOwnerUsedCount} using fallback owner
            </Badge>
          )}
        </div>
        <span className="text-xs text-muted-foreground">
          Started {_relativeTime(progress.startedAt)}
        </span>
      </div>

      {/* Progress bar */}
      <_ProgressBar progress={progress} />

      {/* Counters */}
      <_Counters
        progress={progress}
        selectedCategory={drillDown}
        onSelectCategory={setDrillDown}
      />

      {/* SCH-3.5: drill-down panel */}
      {drillDown && <DrillDownPanel syncRunId={syncRunId} category={drillDown} />}

      {/* Paused banner */}
      {progress.status === 'paused' && (
        <_PausedBanner reason={progress.pausedReason} />
      )}

      {/* Active-batch hint — admin would otherwise stare at a non-changing
          progress bar for several minutes while the orchestrator walks SCH. */}
      {progress.status === 'processing_batch' && (
        <_ProcessingBanner totalCatalog={progress.totalCatalog} />
      )}

      {/* Recent failures */}
      {progress.recentFailures.length > 0 && (
        <_FailuresAccordion failures={progress.recentFailures} />
      )}

      {/* CTAs */}
      <div className="flex items-center justify-end gap-2 border-t pt-4">
        {readOnly ? (
          <>
            <span className="text-xs italic text-muted-foreground mr-auto">
              View only — run already finished
            </span>
            <Button variant="outline" onClick={onClose}>Close</Button>
          </>
        ) : progress.status === 'completed' ? (
          <Button onClick={onClose}>Done</Button>
        ) : progress.status === 'paused' ? (
          // Paused runs always show Resume — admin manually continues even
          // if the run was originally started in auto-continue mode.
          <_ContinueOrResumeButton
            label="Resume backfill"
            disabled={continueDisabled}
            isPending={continueMutation.isPending || pendingContinueClick}
            onClick={handleContinue}
            canRun={canRun}
            roleLoading={roleLoading}
          />
        ) : progress.autoContinue && !progress.awaitingContinue ? (
          // SCH-3.9 auto-continue mode, actively in-flight (processing_batch /
          // fetching_catalog): hide the Continue button — the backend chains
          // batches itself. Static indicator so the admin knows they don't need
          // to click anything (and that closing the tab is safe).
          //
          // NOTE: gated on !awaitingContinue. An auto run sitting at
          // awaiting_continue means the background task died at a batch boundary
          // (Fly restart/idle/crash) and is NOT chaining — that case falls
          // through to the awaiting_continue branch below, which surfaces a
          // Resume button instead of falsely claiming "Auto-continuing".
          <span className="text-xs italic text-muted-foreground">
            Auto-continuing — close this tab to keep the import running in the background.
          </span>
        ) : progress.awaitingContinue ? (
          progress.autoContinue ? (
            // Stalled auto-continue run: the chain stopped at a batch boundary
            // (background task died). Surface a Resume that re-arms auto mode so
            // the import finishes from the persisted cursor. Without this the
            // admin was deadlocked — the old code hid the button whenever
            // auto_continue was set, regardless of whether anything was running.
            <>
              <span className="mr-auto text-xs italic text-amber-700">
                Auto-continue stalled (the server likely restarted). Resume to
                pick up where it left off — close-tab safe.
              </span>
              <_ContinueOrResumeButton
                label="Resume auto-continue"
                disabled={continueDisabled}
                isPending={continueMutation.isPending || pendingContinueClick}
                onClick={handleResumeAutoContinue}
                canRun={canRun}
                roleLoading={roleLoading}
              />
            </>
          ) : (
            <>
              {/* SCH-3.9 mid-run upgrade: checkbox to convert this gated run
                  into an auto-continue run starting with this click. Only
                  shown when admin can actually run (matches button's gate). */}
              {canRun && (
                <div className="mr-auto flex items-start gap-2">
                  <Checkbox
                    id="sch-upgrade-auto-continue"
                    checked={upgradeToAutoContinue}
                    onCheckedChange={(c) => setUpgradeToAutoContinue(c === true)}
                    disabled={continueDisabled}
                    className="mt-0.5"
                  />
                  <Label
                    htmlFor="sch-upgrade-auto-continue"
                    className="cursor-pointer"
                  >
                    <span className="text-xs font-medium">
                      Run to completion (auto-continue the rest)
                    </span>
                    <span className="mt-0.5 block text-[11px] font-normal text-muted-foreground">
                      Stop clicking Continue — chain the remaining batches
                      automatically. Close-tab safe.
                    </span>
                  </Label>
                </div>
              )}
              <_ContinueOrResumeButton
                label={
                  upgradeToAutoContinue
                    ? 'Continue + run to completion'
                    : `Continue with next ${progress.batchSize}`
                }
                disabled={continueDisabled}
                isPending={continueMutation.isPending || pendingContinueClick}
                onClick={handleContinue}
                canRun={canRun}
                roleLoading={roleLoading}
              />
            </>
          )
        ) : (
          <span className="text-xs italic text-muted-foreground">
            Processing batch — polling every 3s…
          </span>
        )}
        {/* SCH-4.4: Abort button. Visible whenever the run is in-flight
            and the admin has permission. Sits to the right of whatever
            primary CTA is shown above (Continue / Auto-continuing /
            Processing).

            Uses inline two-step confirmation (no nested modal) because the
            BackfillProgressView is itself rendered inside a Radix Dialog —
            opening a nested AlertDialog inside it traps focus on the outer
            dialog and the confirm UI becomes unreachable. */}
        {showAbortButton && !showAbortConfirm && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowAbortConfirm(true)}
            disabled={abortMutation.isPending}
            className="text-destructive hover:text-destructive"
          >
            Abort
          </Button>
        )}
        {showAbortButton && showAbortConfirm && (
          <>
            <span className="text-xs text-muted-foreground">
              Abort this run? Current batch will finish first (~30-60s).
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowAbortConfirm(false)}
              disabled={abortMutation.isPending}
            >
              Keep running
            </Button>
            <Button
              variant="destructive"
              size="sm"
              onClick={handleAbort}
              disabled={abortMutation.isPending}
            >
              {abortMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Aborting…
                </>
              ) : (
                'Confirm abort'
              )}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

// ─── Sub-components ────────────────────────────────────────────────────────

function _ProgressBar({ progress }: { progress: SyncProgressResponse }) {
  // When totalCatalog is unknown (full-catalog backfill mode), default to 0
  // so the bar starts empty/grey. The numeric label below still shows
  // totalProcessed, so admins know work is happening.
  const percent =
    progress.totalCatalog && progress.totalCatalog > 0
      ? Math.round(
          Math.min(1, progress.totalProcessed / progress.totalCatalog) * 100,
        )
      : 0;
  return (
    <div className="space-y-1.5">
      {/* Force the track to bg-muted (an obviously-grey neutral) — the
          default bg-secondary varies by theme and was reading as blue,
          making 0% look identical to 100%. */}
      <Progress value={percent} className="bg-muted" />
      <p className="text-xs text-muted-foreground">
        {progress.totalProcessed} processed
        {progress.totalCatalog ? ` of ${progress.totalCatalog}` : ''}
        {progress.lastSubhubId
          ? ` · last: ${_truncate(progress.lastSubhubId, 24)}`
          : ''}
      </p>
    </div>
  );
}

type DrillDownCategory = 'imported' | 'skipped' | 'failed' | 'fallback';

function _Counters({
  progress,
  selectedCategory,
  onSelectCategory,
}: {
  progress: SyncProgressResponse;
  selectedCategory: DrillDownCategory | null;
  onSelectCategory: (next: DrillDownCategory | null) => void;
}) {
  const toggle = (cat: DrillDownCategory) =>
    onSelectCategory(selectedCategory === cat ? null : cat);
  return (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
      <_CounterCard
        label="Imported"
        value={progress.totalSuccess}
        tone="success"
        active={selectedCategory === 'imported'}
        onClick={
          progress.totalSuccess > 0 ? () => toggle('imported') : undefined
        }
      />
      <_CounterCard
        label="Skipped (unchanged)"
        value={progress.totalSkippedUnchanged}
        tone="muted"
        active={selectedCategory === 'skipped'}
        onClick={
          progress.totalSkippedUnchanged > 0
            ? () => toggle('skipped')
            : undefined
        }
      />
      <_CounterCard
        label="Failed"
        value={progress.totalFailed}
        tone={progress.totalFailed > 0 ? 'warn' : 'muted'}
        active={selectedCategory === 'failed'}
        onClick={progress.totalFailed > 0 ? () => toggle('failed') : undefined}
      />
      <_CounterCard
        label="Fallback owner"
        value={progress.fallbackOwnerUsedCount}
        tone={progress.fallbackOwnerUsedCount > 0 ? 'warn' : 'muted'}
        active={selectedCategory === 'fallback'}
        onClick={
          progress.fallbackOwnerUsedCount > 0
            ? () => toggle('fallback')
            : undefined
        }
      />
    </div>
  );
}

function _CounterCard({
  label,
  value,
  tone,
  active,
  onClick,
}: {
  label: string;
  value: number;
  tone: 'success' | 'warn' | 'muted';
  active?: boolean;
  onClick?: () => void;
}) {
  const toneClass =
    tone === 'success'
      ? 'text-emerald-700'
      : tone === 'warn'
        ? 'text-yellow-700'
        : 'text-foreground';
  const interactive = !!onClick;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!interactive}
      className={cn(
        'rounded-md border bg-card p-2.5 text-left transition-colors',
        interactive
          ? 'hover:border-primary/40 cursor-pointer'
          : 'cursor-default',
        active && 'border-primary bg-primary/[0.03]',
      )}
    >
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className={`text-xl font-semibold ${toneClass}`}>{value}</p>
      {interactive && (
        <p className="text-[10px] text-muted-foreground mt-0.5">
          {active ? 'Hide details' : 'Click to drill down'}
        </p>
      )}
    </button>
  );
}

// ─── SCH-3.5: drill-down panel ────────────────────────────────────────────

interface DrillDownProjectRow {
  subhub_project_id: string | null;
  joules_project_id: string | null;
  extra: Record<string, unknown> | null;
  started_at: string;
}

/**
 * Fetch the per-project rows for the selected drill-down counter. Extracted
 * from the panel component so the underscore-prefixed `_DrillDownPanel`
 * (private-helper naming convention used throughout this file) doesn't
 * trip the `react-hooks/rules-of-hooks` lint rule that requires hook
 * callers to be either named with a capital first letter or prefixed `use`.
 */
function useDrillDownProjectRows(syncRunId: string, category: DrillDownCategory) {
  return useQuery<DrillDownProjectRow[]>({
    queryKey: ['sch-drill-down', syncRunId, category],
    queryFn: async () => {
      let query = supabase
        .from('tenant_integration_sync_history')
        .select('subhub_project_id, joules_project_id, extra, started_at')
        .eq('sync_run_id', syncRunId)
        .eq('phase', 'upsert');

      if (category === 'imported') query = query.eq('status', 'success');
      else if (category === 'skipped') query = query.eq('status', 'skipped_unchanged');
      else if (category === 'failed') query = query.eq('status', 'failed');
      else if (category === 'fallback') {
        query = query
          .eq('status', 'success')
          .filter('extra->>fallback_owner_used', 'eq', 'true');
      }

      const { data, error } = await query
        .order('started_at', { ascending: false })
        .limit(500);
      if (error) {
        console.error('Drill-down query failed:', error);
        return [];
      }
      return (data as DrillDownProjectRow[]) ?? [];
    },
  });
}

// Named without the `_` prefix (unlike the other private helpers in this
// file) because it calls hooks; the `react-hooks/rules-of-hooks` lint rule
// requires hook-calling components to start with an uppercase letter.
function DrillDownPanel({
  syncRunId,
  category,
}: {
  syncRunId: string;
  category: DrillDownCategory;
}) {
  const openProject = (projectId: string) => {
    // Match the app-wide pattern (FollowedProjectsModal etc.) — fires a
    // custom event that the global ProjectModal listener handles. Opens
    // the project in a slide panel without navigation, preserving the
    // SubContractorHub Configure context.
    window.dispatchEvent(
      new CustomEvent('openProjectModal', { detail: { projectId } }),
    );
  };
  const { data: rows, isLoading } = useDrillDownProjectRows(syncRunId, category);

  const heading: Record<DrillDownCategory, string> = {
    imported: 'Imported projects',
    skipped: 'Skipped projects (unchanged on SCH side)',
    failed: 'Failed projects',
    fallback: 'Projects using fallback owner',
  };

  if (isLoading) {
    return (
      <div className="rounded-md border bg-card p-4">
        <p className="text-sm text-muted-foreground flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading {heading[category].toLowerCase()}…
        </p>
      </div>
    );
  }

  if (!rows || rows.length === 0) {
    return (
      <div className="rounded-md border bg-card p-4">
        <p className="text-sm text-muted-foreground">No rows found.</p>
      </div>
    );
  }

  return (
    <div className="rounded-md border bg-card overflow-hidden">
      <div className="border-b bg-muted/50 px-3 py-2 flex items-center justify-between">
        <p className="text-sm font-medium">{heading[category]}</p>
        <p className="text-xs text-muted-foreground">{rows.length} rows</p>
      </div>
      <ul className="max-h-96 overflow-y-auto divide-y">
        {rows.map((r, i) => {
          const name =
            (r.extra?.subhub_name as string | undefined) ??
            r.subhub_project_id ??
            '(unknown)';
          const failureReason = r.extra?.reason as string | undefined;
          const emailRedacted = r.extra?.email_redacted as string | undefined;
          const canNavigate = !!r.joules_project_id;
          return (
            <li
              key={`${r.subhub_project_id ?? i}-${i}`}
              className="px-3 py-2 text-sm flex items-center justify-between gap-2"
            >
              <div className="min-w-0 flex-1">
                <p className="truncate font-medium" title={name}>
                  {name}
                </p>
                {failureReason && (
                  <p className="text-xs text-yellow-700 truncate">
                    {failureReason}
                    {emailRedacted ? ` · ${emailRedacted}` : ''}
                  </p>
                )}
              </div>
              {canNavigate && r.joules_project_id && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => openProject(r.joules_project_id as string)}
                  className="text-xs"
                >
                  Open
                </Button>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function _StatusPill({ status }: { status: SyncProgressStatus }) {
  const map: Record<
    SyncProgressStatus,
    { label: string; tone: string; Icon: typeof Loader2 }
  > = {
    pending: { label: 'Pending', tone: 'bg-muted text-muted-foreground', Icon: Loader2 },
    fetching_catalog: {
      label: 'Fetching catalog',
      tone: 'bg-blue-100 text-blue-800',
      Icon: Loader2,
    },
    processing_batch: {
      label: 'Processing batch',
      tone: 'bg-blue-100 text-blue-800',
      Icon: Loader2,
    },
    awaiting_continue: {
      label: 'Awaiting Continue',
      tone: 'bg-amber-100 text-amber-800',
      Icon: PauseCircle,
    },
    completed: {
      label: 'Completed',
      tone: 'bg-emerald-100 text-emerald-800',
      Icon: CheckCircle2,
    },
    paused: {
      label: 'Paused',
      tone: 'bg-orange-100 text-orange-800',
      Icon: PauseCircle,
    },
    failed: { label: 'Failed', tone: 'bg-red-100 text-red-800', Icon: XCircle },
  };
  const { label, tone, Icon } = map[status];
  const isAnimated = status === 'fetching_catalog' || status === 'processing_batch' || status === 'pending';
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${tone}`}
    >
      <Icon className={`h-3 w-3 ${isAnimated ? 'animate-spin' : ''}`} />
      {label}
    </span>
  );
}

function _ProcessingBanner({ totalCatalog }: { totalCatalog: number | null }) {
  const sizeHint = totalCatalog
    ? `Walking SCH catalog for the ${totalCatalog} target project${totalCatalog === 1 ? '' : 's'}.`
    : "Walking SCH catalog (page-by-page).";
  return (
    <div className="rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900">
      <div className="flex items-start gap-2">
        <Loader2 className="mt-0.5 h-4 w-4 flex-shrink-0 animate-spin text-primary" />
        <div className="space-y-0.5">
          <p className="font-medium">Searching SubContractorHub…</p>
          <p className="text-xs text-blue-800/90">
            {sizeHint} SCH limits each page to 25 projects and each request
            takes ~25s, so this can run for several minutes per batch — the
            page is polling every 3s and will update as soon as records start
            importing. You don't need to keep this tab open.
          </p>
        </div>
      </div>
    </div>
  );
}

function _PausedBanner({ reason }: { reason: string | null }) {
  // Canonical user-abort reason is 'aborted_by_user'; 'user_aborted' /
  // 'user_aborted_manual' are legacy strings on runs aborted before SCH-BUG-2.
  const isUserAbort =
    reason === 'aborted_by_user' ||
    reason === 'user_aborted' ||
    reason === 'user_aborted_manual';
  const message = isUserAbort
    ? 'Aborted by you — start a new sync whenever you’re ready.'
    : reason === 'fallback_owner_unset_after_strikes'
      ? "Set a fallback owner in Connection settings to continue this backfill. Three SCH owner emails couldn't be matched to Joules users."
      : reason === 'orchestrator_inactive'
        ? "The backfill stalled — the server may have restarted. Click Resume to pick up where it left off."
        : reason === 'auth_failed'
          ? "SubContractorHub rejected the stored API key. Re-validate from the Connection tab."
          : 'Backfill paused — see logs for details.';
  return (
    <div className="rounded-md border border-orange-200 bg-orange-50 p-3 text-sm text-orange-900">
      <div className="flex items-start gap-2">
        <PauseCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
        <p>{message}</p>
      </div>
    </div>
  );
}

function _FailuresAccordion({
  failures,
}: {
  failures: SyncProgressFailure[];
}) {
  return (
    <Accordion type="single" collapsible className="rounded-md border">
      <AccordionItem value="failures" className="border-b-0">
        <AccordionTrigger className="px-3 py-2 text-sm hover:no-underline">
          <span className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-yellow-700" />
            <span>
              Recent failures ({failures.length}
              {failures.length === 25 ? '+' : ''})
            </span>
          </span>
        </AccordionTrigger>
        <AccordionContent className="px-3 pb-3">
          <div className="space-y-1.5">
            {failures.map((f, i) => (
              <_FailureRow key={`${f.subhubId}-${i}`} failure={f} />
            ))}
          </div>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
}

function _FailureRow({ failure }: { failure: SyncProgressFailure }) {
  const reasonText =
    failure.reason === 'owner_not_found'
      ? `Owner email ${failure.emailRedacted ?? 'unknown'} not found in this tenant`
      : failure.reason === 'field_mapping_error'
        ? 'Field mapping error'
        : failure.reason === 'db_error'
          ? 'Database write failed'
          : failure.reason;
  return (
    <div className="flex items-start gap-2 rounded border border-yellow-200 bg-yellow-50 p-2 text-xs">
      <AlertTriangle className="mt-0.5 h-3 w-3 flex-shrink-0 text-yellow-700" />
      <div className="space-y-0.5">
        <p className="font-medium text-yellow-900">{failure.subhubName}</p>
        <p className="text-yellow-800">{reasonText}</p>
        {failure.detail && failure.reason === 'db_error' && (
          <p className="break-words font-mono text-[11px] text-yellow-900/90">
            {failure.detail}
          </p>
        )}
        <p className="text-[11px] text-yellow-800/80">
          {_relativeTime(failure.occurredAt)}
        </p>
      </div>
    </div>
  );
}

function _ContinueOrResumeButton({
  label,
  disabled,
  isPending,
  onClick,
  canRun,
  roleLoading,
}: {
  label: string;
  disabled: boolean;
  isPending: boolean;
  onClick: () => void;
  canRun: boolean;
  roleLoading: boolean;
}) {
  const button = (
    <Button onClick={onClick} disabled={disabled} className="gap-2">
      {isPending ? (
        <Loader2 className="h-4 w-4 animate-spin" />
      ) : (
        <PlayCircle className="h-4 w-4" />
      )}
      {label}
    </Button>
  );

  if (canRun || roleLoading) {
    return button;
  }

  // No admin access (lacks manage_settings / system.override): wrap with
  // explanatory tooltip.
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span tabIndex={0}>{button}</span>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs">{ADMIN_REQUIRED_TOOLTIP}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

// ─── Helpers ───────────────────────────────────────────────────────────────

function _relativeTime(iso: string): string {
  try {
    const parsed = parseISO(iso);
    if (Number.isNaN(parsed.getTime())) return iso;
    return formatDistanceToNow(parsed, { addSuffix: true });
  } catch {
    return iso;
  }
}

function _truncate(s: string, n: number): string {
  return s.length > n ? `${s.slice(0, n - 1)}…` : s;
}
